
import Button from '@mui/material/Button'
import Input from '@mui/material/Input'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { addArticlePosts } from 'services/compose'
import { ArticleBase } from 'services/compose/interfaces/ArticleBase'
import { getArticlesFromRssUrl } from 'services/content/articles/actions'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import styles from './RSSArticlesUpload.pcss'

export function RSSArticlesUpload(props: {
  onUploadStarted: () => void,
  onUploadError: (err: string) => void
}) {
  const { onUploadStarted, onUploadError } = props
  const intl = useIntl()
  const [value, setValue] = React.useState('')
  const dispatch = useDispatch<StoreThunkDispatch>()
  const rssFeedUrlInputRef = React.useRef<HTMLInputElement>(null)
  const [loading, setLoading] = React.useState(false)
  const fetch$ = React.useRef<Subject<string>>(undefined)

  React.useEffect(() => {
    fetch$.current = new Subject()
    fetch$.current
      .pipe(
        mergeMap((url: string) => {
          onUploadStarted()
          return dispatch(getArticlesFromRssUrl(url)).pipe(
            catchError(error => observableOf({ error }))
          )
        })
      )
      .subscribe((response: any) => {
        setLoading(false)

        if (response.error) {
          onUploadError(response.error.response.message)
        } else if (response.length === 0) {
          onUploadError('An error occurred while parsing this RSS feed. Please try a different URL.')
        } else {
          dispatch(addArticlePosts(response.map((article: ArticleBase) => ({ article }))))
        }
      })

    return () => {
      fetch$.current?.unsubscribe()
    }
  }, [dispatch, onUploadStarted, onUploadError])

  const loadArticles = (url: string) => {
    try {
      const testUrl = new URL(url)
      fetch$.current?.next(url)
    } catch (_e) {
      dispatch(message(intl.formatMessage({ id: 'errors.invalid-url' }), 'error'))
    }
    setValue('')
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const url = (e.target as HTMLInputElement).value.trim()
    if (url && e.key === 'Enter') {
      loadArticles(url)
    }
  }

  const onChange = (e: any) => {
    setValue(e.target.value)
  }

  const createPosts = () => {
    const url = rssFeedUrlInputRef.current?.value
    loadArticles(url || '')
  }

  return (
    <div className={styles.container} data-testid="rss-article-upload">
      <Input
        disableUnderline
        value={value}
        inputRef={rssFeedUrlInputRef}
        classes={{ root: styles['search-input'] }}
        placeholder={intl.formatMessage({
          id: 'composer.labels.submit-rss-feed-url'
        })}
        name="rss-feed-url"
        onKeyDown={onKeyDown}
        onChange={onChange}
      />
      <div>
        <Button
          color="primary"
          variant="contained"
          disabled={loading || !value.trim()}
          className={styles.btn}
          onClick={createPosts}
        >
          <FormattedMessage id="composer.multi-post.actions.upload-articles" />
        </Button>
      </div>
      <div className={styles.link}>
        <Link to="/content/search/sources">
          <FormattedMessage id="composer.multi-post.actions.find-feeds" />
        </Link>
      </div>
    </div>
  )
}

export default RSSArticlesUpload
